import type { AvailableShift, PeriodWorkerReadiness, ReminderKind, SchedulePeriodView, ShiftPreferences, ShiftTypeKey } from '../types.js'

type ShiftPeriod = ShiftTypeKey

/**
 * The month readiness panel's pure parts (docs/plans/month-readiness-plan.md): the four count tiles, the
 * timeline stepper, the readiness table's filters, and the published shifts grouped by day. Every number
 * comes from the API's view; nothing here decides who is on the roster or who bids.
 */

export type ReadinessDetail = 'workers' | 'shifts'
export type ReadinessFilter = 'all' | 'to_remind' | 'no_preferences' | 'not_submitted' | 'stale' | 'assigned' | 'part_time'

/**
 * What a tile's colour says. None for a plain figure; `act` (gold) for something staff can still do something about -
 * preferences missing while the month takes them; `problem` (coral) for something that will change the build -
 * preferences missing after the close, days with nothing published. Selection is shown separately, never by colour.
 */
export type TileTone = 'none' | 'act' | 'problem'

export interface ReadinessTile {
  key: 'roster' | 'preferences' | 'drafts' | 'shifts'
  label: string
  value: string
  sub: string
  tone: TileTone
  opens: { detail: ReadinessDetail; filter: ReadinessFilter }
}

function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`
}

/** The preferences tile: gold while a reminder can still help, coral once the close has passed and the build will draft the missing asks from last month's preferences or assign them last. */
function preferencesTile(view: SchedulePeriodView, canRemind: boolean): ReadinessTile {
  const missing = view.bidding - view.preferencesSaved
  const base = { key: 'preferences' as const, label: 'Preferences saved', value: `${view.preferencesSaved} of ${view.bidding}` }
  if (view.bidding === 0) {
    return { ...base, sub: 'Nobody on the roster bids', tone: 'none', opens: { detail: 'workers', filter: 'all' } }
  }
  if (missing === 0) {
    return { ...base, sub: 'Everyone who bids has saved', tone: 'none', opens: { detail: 'workers', filter: 'all' } }
  }
  const haveNot = `${plural(missing, 'worker')} ${missing === 1 ? "hasn't" : "haven't"} saved`
  if (view.status === 'released') {
    return { ...base, sub: haveNot, tone: 'none', opens: { detail: 'workers', filter: 'no_preferences' } }
  }
  if (view.acceptingPreferences) {
    const remindable = canRemind && view.shiftsPublished > 0
    return { ...base, sub: remindable ? `${haveNot} · Remind them` : haveNot, tone: 'act', opens: { detail: 'workers', filter: remindable ? 'to_remind' : 'no_preferences' } }
  }
  const remindable = canRemind && view.shiftsPublished > 0
  return { ...base, sub: `Workers without preferences are assigned previous months or remaining shifts${remindable ? ' · Remind them' : ''}`, tone: 'problem', opens: { detail: 'workers', filter: remindable ? 'to_remind' : 'no_preferences' } }
}

/** The four tiles, each opening the detail behind it. `canRemind` is staff's: the tile then says Remind them. */
export function readinessTiles(view: SchedulePeriodView, canRemind = false): ReadinessTile[] {
  const notSubmitted = view.rosterSize - view.draftsAccepted
  const perWorker = view.rosterSize > 0 ? Math.round(view.shiftsPublished / view.rosterSize) : 0
  const draftParts = [view.draftsBuilt > 0 ? `${view.draftsBuilt} built, not submitted` : null, view.draftsStale > 0 ? `${view.draftsStale} out of date` : null].filter((part): part is string => part !== null)
  return [
    { key: 'roster', label: 'On the roster', value: String(view.rosterSize), sub: `${view.bidding} bid · ${view.assigned} assigned`, tone: 'none', opens: { detail: 'workers', filter: 'all' } },
    preferencesTile(view, canRemind),
    {
      key: 'drafts',
      label: 'Drafts submitted',
      value: `${view.draftsAccepted} of ${view.rosterSize}`,
      sub: draftParts.length > 0 ? draftParts.join(' · ') : notSubmitted === 0 ? 'Everyone has submitted' : `${plural(notSubmitted, 'worker')} with no draft`,
      // A count only: the build works from preferences, so a draft out of date or unsubmitted changes nothing in it.
      tone: 'none',
      opens: { detail: 'workers', filter: view.draftsStale > 0 ? 'stale' : notSubmitted > 0 ? 'not_submitted' : 'all' },
    },
    {
      key: 'shifts',
      label: 'Shifts published',
      value: String(view.shiftsPublished),
      sub: view.shiftsPublished === 0 ? 'Nothing published yet' : `≈ ${perWorker} per worker · ${view.daysWithoutShifts === 0 ? 'every day covered' : `${plural(view.daysWithoutShifts, 'day')} with none`}`,
      tone: view.shiftsPublished === 0 || view.daysWithoutShifts > 0 ? 'problem' : 'none',
      opens: { detail: 'shifts', filter: 'all' },
    },
  ]
}

export type StepState = 'done' | 'next' | 'later'

export interface TimelineStep {
  key: 'open' | 'close' | 'lock' | 'release' | 'window'
  label: string
  at: string
  state: StepState
  /** What the step means, in a sentence. */
  meaning: string
}

/** The month's dates in order - the bid window's opening first where the facility keeps one - each done, the next one coming, or later. */
export function timelineSteps(view: SchedulePeriodView, now: Date): TimelineStep[] {
  const opening: Omit<TimelineStep, 'state'>[] = view.preferencesOpenAt ? [{ key: 'open', label: 'Bidding opens', at: view.preferencesOpenAt, meaning: 'Workers who bid are told, and can save preferences until the close.' }] : []
  const steps: Omit<TimelineStep, 'state'>[] = [
    ...opening,
    { key: 'close', label: view.preferencesOpenAt ? 'Bidding closes' : 'Preferences close', at: view.preferencesCloseAt, meaning: 'After this, workers can no longer save preferences for the month.' },
    { key: 'lock', label: 'Draft locks', at: view.draftLocksAt, meaning: 'The drafts the run builds from stand as they are.' },
    { key: 'release', label: 'Release', at: view.releaseAt, meaning: 'Workers see their month once you release the run. Nothing reaches them before.' },
    { key: 'window', label: 'Change window closes', at: view.changeWindowClosesAt, meaning: 'Shift-type changes and swaps asked for after the release are settled at this close.' },
  ]
  const nextIndex = steps.findIndex((step) => Date.parse(step.at) > now.getTime())
  return steps.map((step, index) => ({ ...step, state: nextIndex === -1 || index < nextIndex ? 'done' : index === nextIndex ? 'next' : 'later' }))
}

const DAY_MS = 24 * 60 * 60 * 1000

const HOUR_MS = 60 * 60 * 1000

/** "in 5 hours", "in 3 days", "2 days ago". */
export function relativeDay(at: string, now: Date): string {
  const ms = Date.parse(at) - now.getTime()
  if (Math.abs(ms) < DAY_MS) {
    const hours = Math.max(1, Math.round(Math.abs(ms) / HOUR_MS))
    return ms >= 0 ? `in ${plural(hours, 'hour')}` : `${plural(hours, 'hour')} ago`
  }
  const days = Math.round(ms / DAY_MS)
  return days > 0 ? `in ${plural(days, 'day')}` : `${plural(-days, 'day')} ago`
}

/** A month nobody on the roster bids for (every tier assigned), not yet built: it waits on its shifts, never on preferences. */
export function nobodyBids(view: Pick<SchedulePeriodView, 'status'> & Partial<Pick<SchedulePeriodView, 'bidding' | 'rosterSize'>>): boolean {
  return view.bidding === 0 && (view.rosterSize ?? 1) > 0 && (view.status === 'bidding' || view.status === 'closed')
}

/** The line beside the status chip: whether workers can still save preferences, and what that means for the month. */
export function preferencesWindowLine(view: Pick<SchedulePeriodView, 'acceptingPreferences' | 'status' | 'preferencesCloseAt' | 'preferencesOpenAt' | 'timezone' | 'buildAtClose'> & Partial<Pick<SchedulePeriodView, 'bidding' | 'rosterSize'>>, now: Date = new Date()): string {
  // Nobody on the roster bids (every tier here is assigned): there are no preferences to wait for.
  if (nobodyBids(view)) {
    return 'No worker on the roster bids - every tier here is assigned - so there are no preferences to wait for. Build the month once its shifts are published.'
  }
  // Before the close and with nothing built yet, say the engine will build it then.
  const engineBuilds = view.buildAtClose && view.status === 'bidding' ? ' The engine builds the month then; you release it.' : ''
  if (view.preferencesOpenAt && Date.parse(view.preferencesOpenAt) > now.getTime()) {
    return `Bidding opens ${formatInZone(view.preferencesOpenAt, view.timezone)} and runs until ${formatInZone(view.preferencesCloseAt, view.timezone)}.${engineBuilds}`
  }
  if (!view.acceptingPreferences) {
    return 'Preferences are closed: no more can be saved for this month.'
  }
  const until = formatInZone(view.preferencesCloseAt, view.timezone)
  return view.status === 'released' ? `Released before preferences close (${until}): a preference saved now no longer changes this month.` : `Workers can save preferences until ${until}.${engineBuilds}`
}

/** "Nov 3, 12:00 AM" in the facility's zone. */
export function formatInZone(at: string, timezone: string): string {
  return new Date(at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: timezone })
}

/** "EST" or "GMT-5" for the zone at that moment. */
export function zoneAbbreviation(at: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' }).formatToParts(new Date(at)).find((part) => part.type === 'timeZoneName')?.value ?? timezone
}

export const READINESS_FILTERS: readonly { key: ReadinessFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'to_remind', label: 'To remind' },
  { key: 'no_preferences', label: 'No preferences' },
  { key: 'not_submitted', label: 'Not submitted' },
  { key: 'stale', label: 'Out of date' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'part_time', label: 'Part time' },
]

/** Whether a row belongs under a filter. "No preferences" means a worker who bids and saved none: an assigned tier needs none. */
export function matchesFilter(worker: PeriodWorkerReadiness, filter: ReadinessFilter): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'to_remind':
      return worker.reminder !== null
    case 'no_preferences':
      return worker.bids && worker.preferences === null
    case 'not_submitted':
      return worker.draft?.status !== 'accepted'
    case 'stale':
      return worker.draft?.isStale === true
    case 'assigned':
      return !worker.bids
    case 'part_time':
      return worker.employmentType !== 'full_time'
  }
}

export function filterCounts(workers: readonly PeriodWorkerReadiness[]): Record<ReadinessFilter, number> {
  return Object.fromEntries(READINESS_FILTERS.map(({ key }) => [key, workers.filter((worker) => matchesFilter(worker, key)).length])) as Record<ReadinessFilter, number>
}

/** What the Remind button will ask of the worker, for its title and the row's note. */
export const REMINDER_ASKS: Record<ReminderKind, string> = {
  save_preferences: 'Asks them to save their preferences before they close',
  preferences_closed: 'Tells them preferences have closed: without them, they are assigned their previous month’s preferences or the remaining shifts',
}

/** The least time between two reminders to one worker about a month, as the API keeps it. */
export const REMINDER_INTERVAL_MS = 24 * 60 * 60 * 1000

/** True while a reminder sent at `remindedAt` still blocks another. */
export function remindedRecently(remindedAt: string | null, now: Date): boolean {
  return remindedAt !== null && now.getTime() - Date.parse(remindedAt) < REMINDER_INTERVAL_MS
}

export const SHIFT_TYPE_ORDER: readonly ShiftPeriod[] = ['day', 'evening', 'night']

export interface PublishedDay {
  date: string
  /** Seats published per shift type that day. */
  seats: Record<ShiftPeriod, number>
  /** Of them, the seats someone holds (`SeatFill`). */
  filled: Record<ShiftPeriod, number>
  shifts: AvailableShift[]
}

/**
 * Who holds each seat of the month: from the released month as the board holds it (swaps and pickups applied), else
 * from the latest run, which is not yet the month; with no run, nobody - every seat is open.
 */
export interface SeatFill {
  basis: 'released' | 'run' | 'none'
  /** Seat id (the published shift's `id`) to the worker's name. */
  byShift: ReadonlyMap<string, string>
}

export const NO_SEAT_FILL: SeatFill = { basis: 'none', byShift: new Map() }

/** The seats held, from rows of a worker's name and their assignments (a run's results, or the board's month). */
export function seatFillOf(basis: SeatFill['basis'], rows: readonly { name: string; assignments: readonly { shiftId: string }[] }[]): SeatFill {
  return { basis, byShift: new Map(rows.flatMap((row) => row.assignments.map((assignment) => [assignment.shiftId, row.name] as const))) }
}

export const SEAT_FILL_BASIS_TEXT: Record<SeatFill['basis'], string> = {
  released: 'Filled from the released month, with its swaps and pickups.',
  run: 'Filled from the latest run, not yet released: nothing is final until you release it.',
  none: 'No run built yet for this month, so every seat is open.',
}

/** Every day of the month with what is published on it, the empty days included, plus the options for the filters. */
export function publishedShiftsByDay(shifts: readonly AvailableShift[], period: string, filter: { role: string | null; unitId: string | null } = { role: null, unitId: null }, fill: SeatFill = NO_SEAT_FILL): { days: PublishedDay[]; roles: string[]; units: string[] } {
  const inMonth = shifts.filter((shift) => shift.date.slice(0, 7) === period)
  const roles = [...new Set(inMonth.map((shift) => shift.role).filter((role): role is NonNullable<typeof role> => Boolean(role)))].sort()
  const units = [...new Set(inMonth.map((shift) => shift.unitId))].sort()
  const shown = inMonth.filter((shift) => (filter.role === null || shift.role === filter.role || !shift.role) && (filter.unitId === null || shift.unitId === filter.unitId))
  const [year, month] = period.split('-').map(Number)
  const length = new Date(Date.UTC(year ?? 1970, month ?? 1, 0)).getUTCDate()
  const days = Array.from({ length }, (_, index) => {
    const date = `${period}-${String(index + 1).padStart(2, '0')}`
    const onDay = shown.filter((shift) => shift.date === date).sort((a, b) => a.startTime.localeCompare(b.startTime))
    const seats = { day: 0, evening: 0, night: 0 } as Record<ShiftPeriod, number>
    const filled = { day: 0, evening: 0, night: 0 } as Record<ShiftPeriod, number>
    for (const shift of onDay) {
      if (shift.period in seats) {
        seats[shift.period as ShiftPeriod] += 1
        if (fill.byShift.has(shift.id)) {
          filled[shift.period as ShiftPeriod] += 1
        }
      }
    }
    return { date, seats, filled, shifts: onDay }
  })
  return { days, roles, units }
}

export interface ShiftTypeDemand {
  shiftType: ShiftPeriod
  /** Seats published of the type in the month (under the filters), and of them the ones held. */
  seats: number
  filled: number
  /** Workers who put it first. */
  firstChoice: number
  /** Workers who will do it at all. */
  willDo: number
}

/** Supply beside demand per shift type: seats published against how many workers' saved preferences put it first or will do it. */
export function shiftTypeDemand(days: readonly PublishedDay[], preferences: readonly ShiftPreferences[]): ShiftTypeDemand[] {
  return SHIFT_TYPE_ORDER.map((shiftType) => ({
    shiftType,
    seats: days.reduce((sum, day) => sum + day.seats[shiftType], 0),
    filled: days.reduce((sum, day) => sum + day.filled[shiftType], 0),
    firstChoice: preferences.filter((entry) => entry.shiftTypes.find((choice) => choice.willDo)?.shiftType === shiftType).length,
    willDo: preferences.filter((entry) => entry.shiftTypes.some((choice) => choice.shiftType === shiftType && choice.willDo)).length,
  }))
}
